import React, {FC} from 'react';
import styled from 'styled-components';

import {renderCrmSyncSourceImage} from '../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {
  buildContactsManagerAccountSyncsUrl,
  buildContactsManagerSyncsUrl,
} from '../../../../../../../src/helpers/contactsManagerHelpers';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../src/models/api/crmSyncModel';
import {SyncJobModel} from '../../../../../../../src/models/api/jobModel';
import {ContactsManagerPanelId} from '../../../../../../../src/store/panels/panelStateBase';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../../../srcCommon/formatters/dateTime/relativeDateTimeFormatter';
import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {fontWeights} from '../../../../../../shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

interface CrmSyncHeaderDetailsProps {
  panelId: ContactsManagerPanelId;
  crmSyncs: ReadonlyArray<CrmSyncModel>;
  activeSyncJobs: ReadonlyArray<SyncJobModel>;
  hasMicrosoftDynamicsAccountSync: boolean;
  hasCrmSyncFullPageSalesforce: boolean;
  hasHubspotContactSync: boolean;
  hasOutlookDirectorySync: boolean;
  navigateToLink: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledCrmSyncWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  margin-left: 4px;
`;

const StyledSalesforceCrmSyncDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
    color: ${(p) => p.theme.greys.shade80};
  }
`;

const StyledSyncInProgressWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  color: ${(p) => p.theme.interactiveText};
  margin-left: 2px;
  font-weight: ${fontWeights.semibold};

  &:hover {
    text-decoration: underline;
  }
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  margin-right: 4px;
`;

/*
 * Component.
 */

export const CrmSyncHeaderDetails: FC<CrmSyncHeaderDetailsProps> = (props) => {
  const {
    navigateToLink,
    activeSyncJobs,
    crmSyncs,
    hasMicrosoftDynamicsAccountSync,
    hasCrmSyncFullPageSalesforce,
    hasHubspotContactSync,
    hasOutlookDirectorySync,
  } = props;

  const onSyncClick = (source?: CrmSyncSourcesEnum) => {
    if (!source) {
      return null;
    }
    if (
      (source === CrmSyncSourcesEnum.SALESFORCE && !hasCrmSyncFullPageSalesforce) ||
      (source === CrmSyncSourcesEnum.HUBSPOT && !hasHubspotContactSync)
    ) {
      return navigateToLink(buildContactsManagerAccountSyncsUrl(source));
    }
    return navigateToLink(buildContactsManagerSyncsUrl(source));
  };

  const shouldShowDetailsForSource = (crmSync?: CrmSyncModel) => {
    if (!crmSync || crmSync.status === CrmSyncStatusesEnum.DISABLED) {
      return false;
    }
    switch (crmSync.source) {
      case CrmSyncSourcesEnum.SALESFORCE:
        return crmSync.resourceType === CrmSyncIntegrationResourceTypesEnum.CARD
          ? hasCrmSyncFullPageSalesforce
          : true;
      case CrmSyncSourcesEnum.HUBSPOT:
        return crmSync.resourceType === CrmSyncIntegrationResourceTypesEnum.CARD
          ? hasHubspotContactSync
          : true;
      case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
        return hasMicrosoftDynamicsAccountSync;
      case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
        return hasOutlookDirectorySync;
      default:
        return false;
    }
  };

  if (crmSyncs.length === 0) {
    return null;
  }
  return <>{renderCrmSyncDetails(crmSyncs, activeSyncJobs, onSyncClick, shouldShowDetailsForSource)}</>;
};

/*
 * Helpers.
 */

function renderCrmSyncDetails(
  crmSyncs: ReadonlyArray<CrmSyncModel>,
  activeSyncJobs: ReadonlyArray<SyncJobModel>,
  onSyncClick: HandlerOf<CrmSyncSourcesEnum>,
  shouldShowDetailsForSource: (crmSync?: CrmSyncModel | undefined) => boolean,
) {
  return crmSyncs.map((crmSync) => {
    if (!shouldShowDetailsForSource(crmSync)) {
      return null;
    }
    const isInProgress = activeSyncJobs.some((job) => job.sync_id === crmSync.id);
    return renderHeaderDetails(crmSync, isInProgress, () => onSyncClick(crmSync.source));
  });
}

function renderHeaderDetails(crmSync: CrmSyncModel, isInProgress: boolean, onSyncClick: Handler) {
  // If the sync is not in progress and we don't know the last synced OR the status is disabled.
  if ((!isInProgress && !crmSync.lastSyncedAt) || crmSync.status === CrmSyncStatusesEnum.DISABLED) {
    return null;
  }
  return (
    <StyledWrapperDiv key={crmSync.source}>
      <span>・</span>
      <StyledCrmSyncWrapperDiv>
        {renderCrmSyncSourceImage(crmSync.source)}
        <StyledSalesforceCrmSyncDetailsWrapperDiv onClick={onSyncClick}>
          {!isInProgress && renderLastSyncedDetails(crmSync)}
          {isInProgress && renderInProgressDetails()}
        </StyledSalesforceCrmSyncDetailsWrapperDiv>
      </StyledCrmSyncWrapperDiv>
    </StyledWrapperDiv>
  );
}

function renderLastSyncedDetails(crmSync: CrmSyncModel) {
  return (
    <>
      <Trans id="crm-header-last-synced-last">Last synced</Trans>{' '}
      <RelativeDateTimeFormatter value={crmSync.lastSyncedAt} format={RelativeDateTimeFormatsEnum.FULL} />
    </>
  );
}

function renderInProgressDetails() {
  return (
    <StyledSyncInProgressWrapperDiv>
      <Trans id="crm-header-sync-in-progress">Sync in progress...</Trans>
    </StyledSyncInProgressWrapperDiv>
  );
}
